import { useEffect, useState } from 'react'

import { createClient } from '@supabase/supabase-js'
import classNames from 'classnames'
import RoundedButton from 'components/Atoms/Buttons/RoundedButton/RoundedButton'
import { ButtonVariants } from 'constants/index'
import { useNavigate, useParams } from 'react-router-dom'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''
const supabase = createClient(supabaseUrl, supabaseKey)

type Estado = 'cargando' | 'disponible' | 'respondido' | 'finalizado' | 'error'

const s3BaseUrl = process.env.REACT_APP_URL_S3 || ''

export default function PaymentCicloBono() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [estado, setEstado] = useState<Estado>('cargando')

  useEffect(() => {
    const verificarEstadoUsuario = async () => {
      try {
        const { data, error } = await supabase
          .from('votos')
          .select('user_uuid')
          .eq('user_uuid', token)

        if (error) throw error

        if (data && data.length > 0) {
          setEstado('respondido')
        } else {
          setEstado('disponible')
        }
      } catch (error) {
        console.error('Error al verificar:', error)
        setEstado('error')
      }
    }

    if (token) verificarEstadoUsuario()
  }, [token])

  const handleVoto = async (opcionElegida: string) => {
    setEstado('cargando')

    try {
      const { error } = await supabase
        .from('votos')
        .insert([{ user_uuid: token, opcion: opcionElegida }])

      if (error) {
        if (error.code === '23505') {
          setEstado('respondido')
        } else if (
          error.code === '42501' ||
          error.message.includes('row-level security')
        ) {
          setEstado('finalizado')
        } else {
          throw error
        }
      } else {
        const searchParams = new URLSearchParams({ option: opcionElegida })
        navigate(`/payment-ciclobono/${token}/ready?${searchParams.toString()}`)
      }
    } catch (error) {
      console.error('Error al guardar el voto:', error)
      setEstado('error')
    }
  }

  const containerClassName = classNames(
    'min-h-screen',
    'flex',
    'flex-col',
    'items-center',
    'justify-center',
    'px-4',
    'py-8',
    'sm:px-6',
    'lg:px-8',
    'bg-gradient-to-br',
    'from-grey-500',
    'via-white',
    'to-green-700',
  )

  const cardClassName = classNames(
    'w-full',
    'max-w-md',
    'sm:max-w-lg',
    'bg-white',
    'rounded-3xl',
    'shadow-lg',
    'p-6',
    'sm:p-10',
    'text-center',
    'space-y-6',
  )

  const titleClassName = classNames(
    'text-2xl',
    'sm:text-3xl',
    'font-bold',
    'text-black',
  )

  const subtitleClassName = classNames(
    'text-base',
    'sm:text-lg',
    'text-grey-800',
    'leading-relaxed',
  )

  const iconContainerClassName = classNames(
    'w-20',
    'h-20',
    'sm:w-24',
    'sm:h-24',
    'mx-auto',
    'rounded-full',
    'flex',
    'items-center',
    'justify-center',
    'mb-4',
  )

  if (estado === 'cargando') {
    return (
      <div className={containerClassName}>
        <div className={cardClassName}>
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-grey-500 rounded-full" />
              <div className="absolute top-0 left-0 w-16 h-16 sm:w-20 sm:h-20 border-4 border-blue-800 rounded-full animate-spin border-t-transparent" />
            </div>
            <h2 className={titleClassName}>Verificando enlace...</h2>
            <p className={subtitleClassName}>
              Estamos comprobando tu solicitud de forma segura
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (estado === 'finalizado') {
    return (
      <div className={containerClassName}>
        <div className={cardClassName}>
          <div className={`${iconContainerClassName} bg-grey-500`}>
            <svg
              className="w-10 h-10 sm:w-12 sm:h-12 text-grey-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className={titleClassName}>Proceso finalizado</h2>
          <p className={subtitleClassName}>
            El periodo para canjear tu solicitud ha terminado.
          </p>
          <div className="bg-grey-500 rounded-2xl p-4 sm:p-6 mt-4">
            <p className="text-sm sm:text-base text-grey-900">
              Si crees que hay un error o necesitas ayuda, por favor ponte en
              contacto con{' '}
              <a
                href="mailto:soporte@ciclogreen.com"
                className="text-blue-800 font-semibold hover:underline"
              >
                soporte@ciclogreen.com
              </a>
            </p>
          </div>
          <div className="pt-4">
            <img
              src={`${s3BaseUrl}/images/logo_10years.png`}
              alt="Ciclogreen"
              className="h-10 sm:h-12 mx-auto"
            />
          </div>
        </div>
      </div>
    )
  }

  if (estado === 'respondido') {
    return (
      <div className={containerClassName}>
        <div className={cardClassName}>
          <div className={`${iconContainerClassName} bg-green-700`}>
            <svg
              className="w-10 h-10 sm:w-12 sm:h-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className={titleClassName}>Solicitud procesada</h2>
          <p className={subtitleClassName}>
            Ya hemos registrado tu respuesta anteriormente.
          </p>
          <div className="bg-grey-500 rounded-2xl p-4 sm:p-6 mt-4">
            <p className="text-sm sm:text-base text-grey-900">
              Si crees que hay un error o necesitas ayuda, por favor ponte en
              contacto con{' '}
              <a
                href="mailto:soporte@ciclogreen.com"
                className="text-blue-800 font-semibold hover:underline"
              >
                soporte@ciclogreen.com
              </a>
            </p>
          </div>
          <div className="pt-4">
            <img
              src={`${s3BaseUrl}/images/logo_10years.png`}
              alt="Ciclogreen"
              className="h-10 sm:h-12 mx-auto"
            />
          </div>
        </div>
      </div>
    )
  }

  if (estado === 'error') {
    return (
      <div className={containerClassName}>
        <div className={cardClassName}>
          <div className={`${iconContainerClassName} bg-red-100`}>
            <svg
              className="w-10 h-10 sm:w-12 sm:h-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className={titleClassName}>Error de conexión</h2>
          <p className={subtitleClassName}>
            Por favor, inténtalo de nuevo más tarde.
          </p>
          <RoundedButton
            variant={ButtonVariants.PRIMARY}
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Reintentar
          </RoundedButton>
        </div>
      </div>
    )
  }

  return (
    <div className={containerClassName}>
      <div className={cardClassName}>
        <div
          className={`${iconContainerClassName} bg-gradient-to-br from-blue-800 to-green-800`}
        >
          <svg
            className="w-10 h-10 sm:w-12 sm:h-12 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        <h2 className={titleClassName}>¿Cómo canjear tu CicloBono?</h2>
        <p className={subtitleClassName}>
          La forma más sencilla: <b>¡Automáticamente a través de la app!</b>{' '}
          estamos ultimando una nueva versión que incluirá la sección
          ''Monedero'' donde podrás ver el saldo con todos tus ahorros generados
          y canjearlo por tus tarjetas regalo favoritas, además seguirás
          acumulando saldo con cada km que registres en coche compartido.
        </p>
        <p className={subtitleClassName}>
          La otra opción: <b>Vía email</b>, te enviaremos un correo con un link
          que podrás usar para canjear tu ahorro generado hasta 2025 por
          tarjetas regalo.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <RoundedButton
            variant={ButtonVariants.PRIMARY}
            onClick={() => handleVoto('APP')}
            className="flex-1 w-full sm:w-auto"
          >
            Canjear desde la nueva app
          </RoundedButton>
          <RoundedButton
            variant={ButtonVariants.SECONDARY}
            onClick={() => handleVoto('RETIRA')}
            className="flex-1 w-full sm:w-auto"
          >
            Canjear desde link vía mail
          </RoundedButton>
        </div>

        <div className="pt-6 border-t border-grey-500 mt-6">
          <p className="text-xs sm:text-sm text-grey-700">
            Al confirmar, aceptas los términos y condiciones del programa Ciclo
            Bono
          </p>
        </div>

        <div className="pt-4">
          <img
            src={`${s3BaseUrl}/images/logo_10years.png`}
            alt="Ciclogreen"
            className="h-10 sm:h-12 mx-auto"
          />
        </div>
      </div>
    </div>
  )
}
